import React, { useEffect, useState, useRef } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import { CountrySelect, StateSelect, GetState, GetCountries } from 'react-country-state-city';
import { Country as CountryType } from 'react-country-state-city/dist/esm/types';
import { FormProvider, RHFTextField } from '@sdf/base';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { useBillingDetailsForm } from '../../hooks/payment';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { LOCATION_DATA_URL } from '../../utils/constant';

interface Props {
  handleOnCancel?: () => void;
  handleInitiatePurchase?: () => Promise<void>;
  updateDetails?: boolean;
  showCancel?: boolean;
}

declare global {
  interface Window {
    google?: any;
  }
}

export const BillingDetails: React.FC<Props> = ({
  handleOnCancel,
  handleInitiatePurchase,
  updateDetails = false,
  showCancel = true,
}) => {
  const {
    formMethods,
    handleSubmit,
    setValue,
    formState,
    country,
    state,
    city,
    showStateField,
    setShowStateField,
    isFetchingBillingDetails,
    isSubmitting,
    userBillingData,
  } = useBillingDetailsForm(handleInitiatePurchase);

  const { errors } = formState;

  const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.google?.maps) {
        resolve();
        return;
      }

      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve());
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=beta`;
      script.async = true;
      script.defer = true;

      script.onload = () => resolve();
      script.onerror = (err) => reject(err);

      document.body.appendChild(script);
    });
  };

  const autocompleteRef = useRef<HTMLDivElement>(null);
  const autocompleteInstanceRef = useRef<any>(null); // Store autocomplete instance

  const handleAddressFocus = async () => {
    if (!window.google?.maps?.places) {
      await loadGoogleMapsScript(process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '');
    }

    if (
      autocompleteRef.current &&
      !autocompleteRef.current.getAttribute('data-autocomplete-attached')
    ) {
      const countries = (await GetCountries(LOCATION_DATA_URL)) as CountryType[];
      const countryCode = countries.find((c) => c.name === country.name)?.iso2;

      const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, {
        types: ['address'],
        fields: ['address_components'],
        componentRestrictions: countryCode ? { country: [countryCode] } : {},
      });

      autocompleteInstanceRef.current = autocomplete;

      autocompleteRef.current.setAttribute('data-autocomplete-attached', 'true');

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const components = place.address_components || [];

        const getComponent = (types: string[]) => {
          const comp = components.find((c: any) => types.some((type) => c.types.includes(type)));
          return comp?.long_name || '';
        };

        const streetNumber = getComponent(['street_number']);
        const route = getComponent(['route']);
        const sublocality = getComponent(['sublocality', 'sublocality_level_1']);
        const cityName = getComponent(['locality', 'administrative_area_level_2']);
        const stateName = getComponent(['administrative_area_level_1']);
        const pincodeNumber = getComponent(['postal_code']);

        const addressLine1 = [streetNumber, route].filter(Boolean).join(' ');
        const addressLine2 = sublocality;

        const composedAddress = [addressLine1, addressLine2].filter(Boolean).join(', ');

        setValue('address', composedAddress);
        setValue('city', cityName || '');
        setValue('state', { id: null, name: stateName || '' });
        setValue('pincode', pincodeNumber || '');
      });
    }
  };

  // Update componentRestrictions when country changes
  useEffect(() => {
    const updateAutocompleteCountry = async () => {
      if (autocompleteInstanceRef.current && country && country.name) {
        // Extract country name before async operation to avoid TypeScript issues
        const countryName = country.name;
        const countries = (await GetCountries(LOCATION_DATA_URL)) as CountryType[];
        const countryCode = countries.find((c: CountryType) => c.name === countryName)?.iso2;
        if (countryCode) {
          autocompleteInstanceRef.current.setComponentRestrictions({ country: [countryCode] });
        } else {
          autocompleteInstanceRef.current.setComponentRestrictions({});
        }
      }
    };
    updateAutocompleteCountry();
  }, [country]);

  useEffect(() => {
    if (country) {
      if (country.name === 'United Kingdom') {
        setShowStateField(false);
      } else {
        GetState(country.id, LOCATION_DATA_URL).then((result) => {
          result.length > 0 ? setShowStateField(true) : setShowStateField(false);
        });
      }
    }
  }, [country]);

  useEffect(() => {
    const countryInput = document.querySelector('.country-input') as HTMLInputElement;
    if (countryInput) {
      countryInput.setAttribute('autocomplete', 'nope');
      countryInput.setAttribute('data-form-type', 'other');
      countryInput.setAttribute('data-lpignore', 'true');
      countryInput.setAttribute('data-1p-ignore', 'true');
      countryInput.setAttribute('aria-autocomplete', 'none');
      countryInput.setAttribute('role', 'textbox');
      countryInput.setAttribute('form', 'fake-form-' + Math.random());
    }

    // Additional autofill prevention for address-related fields
    const addressInput = autocompleteRef.current;
    if (addressInput) {
      addressInput.setAttribute('autocomplete', 'nope');
      addressInput.setAttribute('data-form-type', 'other');
      addressInput.setAttribute('data-lpignore', 'true');
      addressInput.setAttribute('data-1p-ignore', 'true');
      addressInput.setAttribute('aria-autocomplete', 'none');
    }

    // Prevent autofill on all form inputs with more aggressive attributes
    const allInputs = document.querySelectorAll(
      'input[name^="address"], input[name^="city"], input[name^="pincode"], input[name^="name"]'
    );
    allInputs.forEach((input) => {
      const inputElement = input as HTMLInputElement;
      inputElement.setAttribute('autocomplete', 'nope');
      inputElement.setAttribute('data-form-type', 'other');
      inputElement.setAttribute('data-lpignore', 'true');
      inputElement.setAttribute('data-1p-ignore', 'true');
      inputElement.setAttribute('aria-autocomplete', 'none');

      // Add random name suffix to confuse browsers
      if (inputElement.name === 'city') {
        inputElement.setAttribute('name', `city_${Math.random().toString(36).substr(2, 9)}`);
      }
    });
  }, [isFetchingBillingDetails]);

  // Specific effect to prevent city field autofill
  useEffect(() => {
    const timer = setTimeout(() => {
      const cityInput = document.querySelector('input[name="city"]') as HTMLInputElement;
      if (cityInput) {
        cityInput.setAttribute('autocomplete', 'nope');
        cityInput.setAttribute('data-lpignore', 'true');
        cityInput.setAttribute('data-1p-ignore', 'true');
        cityInput.setAttribute('aria-autocomplete', 'none');
        cityInput.setAttribute('role', 'textbox');

        // Disable browser's address autofill specifically
        cityInput.addEventListener('focus', () => {
          cityInput.setAttribute('autocomplete', 'nope');
        });

        cityInput.addEventListener('input', () => {
          cityInput.setAttribute('autocomplete', 'nope');
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [country, isFetchingBillingDetails]);

  // Specific effect to prevent country field autofill
  useEffect(() => {
    const timer = setTimeout(() => {
      const countryInput = document.querySelector('.country-input') as HTMLInputElement;
      if (countryInput) {
        countryInput.setAttribute('autocomplete', 'nope');
        countryInput.setAttribute('data-lpignore', 'true');
        countryInput.setAttribute('data-1p-ignore', 'true');
        countryInput.setAttribute('aria-autocomplete', 'none');
        countryInput.setAttribute('role', 'textbox');
        countryInput.setAttribute('form', 'fake-form-' + Math.random());

        // Disable browser's address autofill specifically
        countryInput.addEventListener('focus', () => {
          countryInput.setAttribute('autocomplete', 'nope');
        });

        countryInput.addEventListener('input', () => {
          countryInput.setAttribute('autocomplete', 'nope');
        });

        countryInput.addEventListener('click', () => {
          countryInput.setAttribute('autocomplete', 'nope');
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [country, isFetchingBillingDetails]);

  return (
    <Paper
      elevation={5}
      sx={{
        width: '100%',
        bgcolor: (theme) => theme.palette.background?.primaryV2,
        border: (theme) => `0.5px solid ${theme.palette.background?.ternaryV2}`,
      }}
    >
      <Typography variant="h1" padding={2}>
        {updateDetails ? 'Update Billing Address' : 'Payment Details'}
      </Typography>

      {!updateDetails && (
        <Divider
          sx={{
            borderColor: (theme) => theme.palette.background?.secondaryBorderV2,
          }}
        />
      )}

      <FormProvider methods={formMethods} onSubmit={handleSubmit}>
        {isFetchingBillingDetails && !userBillingData ? (
          <Stack justifyContent={'center'} alignItems="center" sx={{ minHeight: 350 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box>
            <Grid container spacing={3} p={2}>
              <Grid item xs={12}>
                <Typography sx={styles.label}>Billing Name</Typography>
                <RHFTextField
                  name="name"
                  placeholder="Enter Name"
                  size="small"
                  fullWidth
                  sx={styles.textField}
                  autoComplete="nope"
                  inputProps={{
                    autoComplete: 'nope',
                    'data-form-type': 'other',
                    'data-lpignore': 'true',
                    'data-1p-ignore': 'true',
                    'aria-autocomplete': 'none',
                    form: 'fake-form-' + Math.random(),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography sx={styles.label}>Country</Typography>
                <Box sx={{ position: 'relative' }}>
                  {/* Hidden dummy field to confuse browsers */}
                  <input
                    type="text"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      opacity: 0,
                      pointerEvents: 'none',
                    }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <CountrySelect
                    inputClassName="country-input"
                    defaultValue={country as any}
                    src={LOCATION_DATA_URL}
                    onChange={(selectedCountry) => {
                      setValue('country', selectedCountry);
                      setValue('address', '');
                      setValue('state', { id: null, name: '' });
                      setValue('city', '');
                      setValue('pincode', '');
                    }}
                    placeHolder="Select Country"
                    showFlag={false}
                  />
                </Box>
                {!country.name && errors?.country && (
                  <Typography sx={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    Please select a valid country
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography sx={styles.label}>Billing Address</Typography>

                <RHFTextField
                  name="address"
                  placeholder="Enter Address"
                  size="small"
                  fullWidth
                  inputRef={autocompleteRef}
                  onFocus={handleAddressFocus}
                  sx={styles.textField}
                  autoComplete="nope"
                  inputProps={{
                    autoComplete: 'nope',
                    'data-form-type': 'other',
                    'data-lpignore': 'true',
                    'data-1p-ignore': 'true',
                    'aria-autocomplete': 'none',
                    form: 'fake-form-' + Math.random(),
                  }}
                />
              </Grid>

              {country.id && (
                <>
                  <Grid item xs={12} container>
                    {showStateField && (
                      <Grid item xs={6}>
                        <Typography sx={styles.label}>State</Typography>
                        <Box sx={{ position: 'relative' }}>
                          <StateSelect
                            countryid={country.id}
                            inputClassName="state-input"
                            src={LOCATION_DATA_URL}
                            defaultValue={state as any}
                            onChange={(selectedState) => {
                              setValue('state', selectedState);
                              // Clear city when state changes
                              setValue('city', '');
                            }}
                            onTextChange={(e) => {
                              setValue('state', { id: null, name: e.target.value });
                            }}
                            placeHolder="Select/Enter State"
                          />
                        </Box>
                        {!state?.name && errors?.state && (
                          <Typography sx={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                            Please select a valid state
                          </Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>

                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs={6}>
                      <Typography sx={styles.label}>City</Typography>
                      <Box sx={{ position: 'relative' }}>
                        {/* Hidden dummy field to confuse browsers */}
                        <input
                          type="text"
                          style={{
                            position: 'absolute',
                            left: '-9999px',
                            opacity: 0,
                            pointerEvents: 'none',
                          }}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                        <RHFTextField
                          name="city"
                          placeholder="Enter City"
                          size="small"
                          fullWidth
                          type="text"
                          sx={styles.textField}
                          autoComplete="nope"
                          inputProps={{
                            autoComplete: 'nope',
                            'data-form-type': 'other',
                            'data-lpignore': 'true',
                            'data-1p-ignore': 'true',
                            'aria-autocomplete': 'none',
                            form: 'fake-form-' + Math.random(),
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={styles.label}>ZIP code</Typography>
                      <RHFTextField
                        name="pincode"
                        placeholder="Enter ZIP code"
                        size="small"
                        fullWidth
                        type="text"
                        sx={styles.textField}
                        autoComplete="nope"
                        inputProps={{
                          autoComplete: 'nope',
                          'data-form-type': 'other',
                          'data-lpignore': 'true',
                          'data-1p-ignore': 'true',
                          'aria-autocomplete': 'none',
                          form: 'fake-form-' + Math.random(),
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
            {!updateDetails && <Divider sx={{ borderWidth: 1.5 }} />}
            <Stack direction="row" spacing={2} width="100%" mt={1} mb={1} p={2}>
              {!updateDetails && (
                <Button variant="outlined" onClick={handleOnCancel} sx={styles.cancelBtn} fullWidth>
                  Cancel
                </Button>
              )}
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{
                  ...styles.nextBtn,
                  width: updateDetails ? '30%' : '100%',
                }}
              >
                {updateDetails ? 'Save' : 'Next'}
              </LoadingButton>
            </Stack>
          </Box>
        )}
      </FormProvider>
    </Paper>
  );
};

const styles = {
  label: {
    fontSize: 15,
    fontWeight: 600,
    marginBottom: '8px',
  },
  cancelBtn: {
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
  },
  nextBtn: {
    boxShadow: 'none',
    color: (theme) => theme.palette.text.primary,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '8px',
      borderColor: (theme) => theme.palette.background.ternaryV2,
      backgroundColor: (theme) => theme.palette.background.primaryV2,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.secondaryBorderV2,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
};
